import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  CheckCircle,
  Eye,
  EyeOff,
  Pencil,
  Plus,
  Search,
  Settings2,
  Trash2,
  X
} from 'lucide-react';
import { TopupTemplateService } from '../../services/topupTemplateService';
import LoadingSpinner from '../common/LoadingSpinner';
import { useNotification } from '../common/Notification';

const createEmptyField = (type = TopupTemplateService.FIELD_TYPES.TEXT) => ({
  id: `field_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
  label: '',
  type,
  required: type === TopupTemplateService.FIELD_TYPES.INSTRUCTION ? false : true,
  placeholder: '',
  helpText: '',
  optionsText: ''
});

const createInitialFormData = () => ({
  name: '',
  family: TopupTemplateService.TEMPLATE_FAMILIES.GAMING,
  description: '',
  status: 'active',
  submissionMode: TopupTemplateService.SUBMISSION_MODES.DASHBOARD,
  whatsappNumber: '',
  fields: []
});

const TopupTemplateManagement = () => {
  const { addNotification } = useNotification();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [familyFilter, setFamilyFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [formData, setFormData] = useState(createInitialFormData());
  const [formErrors, setFormErrors] = useState({});

  const loadTemplates = useCallback(async () => {
    try {
      setLoading(true);
      const templatesData = await TopupTemplateService.getAllTemplates();
      setTemplates(templatesData);
    } catch (error) {
      console.error('Erreur lors du chargement des templates Topup:', error);
      addNotification('error', 'Erreur de chargement', 'Impossible de charger les templates Topup');
    } finally {
      setLoading(false);
    }
  }, [addNotification]);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const filteredTemplates = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    return templates.filter((template) => {
      const matchesSearch = !normalizedSearch ||
        template.name?.toLowerCase().includes(normalizedSearch) ||
        template.description?.toLowerCase().includes(normalizedSearch);
      const matchesFamily = familyFilter === 'all' || template.family === familyFilter;
      const matchesStatus = statusFilter === 'all' || template.status === statusFilter;
      return matchesSearch && matchesFamily && matchesStatus;
    });
  }, [familyFilter, searchTerm, statusFilter, templates]);

  const handleOpenModal = (template = null) => {
    if (template) {
      setEditingTemplate(template);
      setFormData({
        name: template.name || '',
        family: template.family || TopupTemplateService.TEMPLATE_FAMILIES.ADVANCED,
        description: template.description || '',
        status: template.status || 'active',
        submissionMode: template.submissionMode || TopupTemplateService.SUBMISSION_MODES.DASHBOARD,
        whatsappNumber: template.whatsappNumber || '',
        fields: (template.fields || []).map((field) => ({
          ...field,
          optionsText: Array.isArray(field.options) ? field.options.join('\n') : ''
        }))
      });
    } else {
      setEditingTemplate(null);
      setFormData(createInitialFormData());
    }

    setFormErrors({});
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingTemplate(null);
    setFormData(createInitialFormData());
    setFormErrors({});
  };

  const updateField = (fieldId, key, value) => {
    setFormData((current) => ({
      ...current,
      fields: current.fields.map((field) => (
        field.id === fieldId ? { ...field, [key]: value } : field
      ))
    }));
  };

  const addField = (type = TopupTemplateService.FIELD_TYPES.TEXT) => {
    setFormData((current) => ({
      ...current,
      fields: [...current.fields, createEmptyField(type)]
    }));
  };

  const removeField = (fieldId) => {
    setFormData((current) => ({
      ...current,
      fields: current.fields.filter((field) => field.id !== fieldId)
    }));
  };

  const validateForm = async () => {
    const nextErrors = {};

    if (!formData.name.trim()) {
      nextErrors.name = 'Le nom du template est requis';
    } else {
      const exists = await TopupTemplateService.templateExistsByName(
        formData.name,
        editingTemplate?.id || null
      );

      if (exists) {
        nextErrors.name = 'Un template avec ce nom existe deja';
      }
    }

    if (Array.isArray(formData.fields) && formData.fields.length > 0) {
      formData.fields.forEach((field, index) => {
        if (!field.label?.trim()) {
          nextErrors[`field_${index}_label`] = 'Le libelle du champ est requis';
        }

        if (
          field.type === TopupTemplateService.FIELD_TYPES.INSTRUCTION &&
          !String(field.helpText || '').trim()
        ) {
          nextErrors[`field_${index}_helpText`] = 'Le contenu de l instruction est requis';
        }

        if (
          field.type === TopupTemplateService.FIELD_TYPES.SELECT &&
          !String(field.optionsText || '').trim()
        ) {
          nextErrors[`field_${index}_options`] = 'Ajoutez au moins une option';
        }
      });
    }

    setFormErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const isValid = await validateForm();
    if (!isValid) {
      return;
    }

    setSubmitting(true);

    try {
      const payload = {
        ...formData,
        fields: formData.fields.map((field) => ({
          id: field.id,
          label: field.label.trim(),
          type: field.type,
          required: field.type === TopupTemplateService.FIELD_TYPES.INSTRUCTION
            ? false
            : Boolean(field.required),
          placeholder: field.type === TopupTemplateService.FIELD_TYPES.INSTRUCTION
            ? ''
            : (field.placeholder?.trim() || ''),
          helpText: field.helpText?.trim() || '',
          options: field.type === TopupTemplateService.FIELD_TYPES.SELECT
            ? String(field.optionsText || '')
                .split('\n')
                .map((option) => option.trim())
                .filter(Boolean)
            : []
        }))
      };

      if (editingTemplate) {
        await TopupTemplateService.updateTemplate(editingTemplate.id, payload);
        addNotification('success', 'Template mis a jour', 'Le template Topup a ete mis a jour');
      } else {
        await TopupTemplateService.createTemplate(payload);
        addNotification('success', 'Template cree', 'Le template Topup a ete cree');
      }

      handleCloseModal();
      await loadTemplates();
    } catch (error) {
      console.error('Erreur lors de la sauvegarde du template Topup:', error);
      addNotification('error', 'Erreur de sauvegarde', 'Impossible de sauvegarder le template Topup');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleStatus = async (templateId) => {
    try {
      await TopupTemplateService.toggleTemplateStatus(templateId);
      addNotification('success', 'Statut mis a jour', 'Le statut du template a ete mis a jour');
      await loadTemplates();
    } catch (error) {
      console.error('Erreur lors du changement de statut du template Topup:', error);
      addNotification('error', 'Erreur', 'Impossible de changer le statut du template');
    }
  };

  const handleDelete = async (template) => {
    if (!window.confirm(`Supprimer le template "${template.name}" ?`)) {
      return;
    }

    try {
      await TopupTemplateService.deleteTemplate(template.id);
      addNotification('success', 'Template supprime', 'Le template Topup a ete supprime');
      await loadTemplates();
    } catch (error) {
      console.error('Erreur lors de la suppression du template Topup:', error);
      addNotification('error', 'Erreur de suppression', 'Impossible de supprimer le template');
    }
  };

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <LoadingSpinner size="xl" />
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6 flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Templates Topup</h1>
          <p className="mt-1 text-gray-600">
            Gere les modeles de formulaire utilises par les services Topup.
          </p>
        </div>

        <button
          type="button"
          onClick={() => handleOpenModal()}
          className="inline-flex items-center rounded-lg bg-primary-600 px-4 py-2 text-white transition-colors hover:bg-primary-700"
        >
          <Plus className="mr-2 h-4 w-4" />
          Nouveau template
        </button>
      </div>

      <div className="mb-6 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Rechercher un template..."
              className="w-full rounded-lg border border-gray-300 py-2 pl-10 pr-4 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
            />
          </div>

          <select
            value={familyFilter}
            onChange={(event) => setFamilyFilter(event.target.value)}
            className="rounded-lg border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
          >
            <option value="all">Toutes les familles</option>
            {Object.values(TopupTemplateService.TEMPLATE_FAMILIES).map((family) => (
              <option key={family} value={family}>
                {TopupTemplateService.getFamilyLabel(family)}
              </option>
            ))}
          </select>

          <select
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
            className="rounded-lg border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
          >
            <option value="all">Tous les statuts</option>
            <option value="active">Actifs</option>
            <option value="inactive">Inactifs</option>
          </select>
        </div>
      </div>

      {filteredTemplates.length === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-300 bg-white p-10 text-center shadow-sm">
          <Settings2 className="mx-auto h-10 w-10 text-gray-300" />
          <p className="mt-4 text-lg font-semibold text-gray-700">Aucun template Topup</p>
          <p className="mt-2 text-sm text-gray-500">
            Cree ton premier template pour lier un service Topup a un vrai formulaire.
          </p>
        </div>
      ) : (
        <div className="grid gap-4 xl:grid-cols-2">
          {filteredTemplates.map((template) => (
            <div
              key={template.id}
              className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm"
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 className="text-lg font-semibold text-gray-900">{template.name}</h2>
                    <span className="rounded-full bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-600">
                      {TopupTemplateService.getFamilyLabel(template.family)}
                    </span>
                    <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${
                      template.status === 'active'
                        ? 'bg-emerald-50 text-emerald-600'
                        : 'bg-gray-100 text-gray-500'
                    }`}>
                      {template.status === 'active' ? 'Actif' : 'Inactif'}
                    </span>
                  </div>
                  <p className="mt-2 text-sm text-gray-500">
                    {template.description || 'Aucune description'}
                  </p>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => handleToggleStatus(template.id)}
                    className="rounded-lg border border-gray-200 p-2 text-gray-500 transition-colors hover:border-gray-300 hover:text-gray-700"
                    title={template.status === 'active' ? 'Desactiver' : 'Activer'}
                  >
                    {template.status === 'active' ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleOpenModal(template)}
                    className="rounded-lg border border-gray-200 p-2 text-gray-500 transition-colors hover:border-gray-300 hover:text-gray-700"
                    title="Modifier"
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(template)}
                    className="rounded-lg border border-red-200 p-2 text-red-500 transition-colors hover:bg-red-50"
                    title="Supprimer"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>

              <div className="mt-4 grid gap-3 sm:grid-cols-2">
                <div className="rounded-xl bg-gray-50 p-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Traitement</p>
                  <p className="mt-1 text-sm font-medium text-gray-700">
                    {TopupTemplateService.getSubmissionModeLabel(template.submissionMode)}
                  </p>
                </div>
                <div className="rounded-xl bg-gray-50 p-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Champs</p>
                  <p className="mt-1 text-sm font-medium text-gray-700">
                    {template.fields?.length || 0} champ(s)
                  </p>
                </div>
              </div>

              <div className="mt-4 rounded-xl border border-gray-100 bg-gray-50 p-4">
                <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500">Apercu du formulaire</p>
                <div className="space-y-2">
                  {(template.fields || []).slice(0, 4).map((field) => (
                    <div key={field.id} className="flex items-center justify-between rounded-lg bg-white px-3 py-2 text-sm">
                      <div>
                        <p className="font-medium text-gray-800">{field.label}</p>
                        <p className="text-xs text-gray-400">
                          {TopupTemplateService.getFieldTypeLabel(field.type)}
                        </p>
                      </div>
                      {field.required && (
                        <CheckCircle className="h-4 w-4 text-emerald-500" />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="max-h-[90vh] w-full max-w-5xl overflow-y-auto rounded-2xl bg-white shadow-2xl">
            <div className="sticky top-0 z-10 flex items-center justify-between border-b border-gray-200 bg-white px-6 py-4">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">
                  {editingTemplate ? 'Modifier le template Topup' : 'Nouveau template Topup'}
                </h2>
                <p className="mt-1 text-sm text-gray-500">
                  Compose le formulaire qui sera attache aux services Topup.
                </p>
              </div>
              <button
                type="button"
                onClick={handleCloseModal}
                className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6 p-6">
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                <div className="xl:col-span-2">
                  <label className="mb-2 block text-sm font-medium text-gray-700">Nom du template *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(event) => setFormData((current) => ({ ...current, name: event.target.value }))}
                    className={`w-full rounded-lg border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500/20 ${
                      formErrors.name ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="Ex: Player ID + WhatsApp"
                  />
                  {formErrors.name && (
                    <p className="mt-1 text-sm text-red-600">{formErrors.name}</p>
                  )}
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">Famille</label>
                  <select
                    value={formData.family}
                    onChange={(event) => setFormData((current) => ({ ...current, family: event.target.value }))}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
                  >
                    {Object.values(TopupTemplateService.TEMPLATE_FAMILIES).map((family) => (
                      <option key={family} value={family}>
                        {TopupTemplateService.getFamilyLabel(family)}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">Mode de traitement</label>
                  <select
                    value={formData.submissionMode}
                    onChange={(event) => setFormData((current) => ({ ...current, submissionMode: event.target.value }))}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
                  >
                    {Object.values(TopupTemplateService.SUBMISSION_MODES).map((mode) => (
                      <option key={mode} value={mode}>
                        {TopupTemplateService.getSubmissionModeLabel(mode)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(event) => setFormData((current) => ({ ...current, description: event.target.value }))}
                    rows={3}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
                    placeholder="Ex: Demande l'identifiant joueur puis le numero WhatsApp."
                  />
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700">Numero WhatsApp</label>
                    <input
                      type="text"
                      value={formData.whatsappNumber}
                      onChange={(event) => setFormData((current) => ({ ...current, whatsappNumber: event.target.value }))}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
                      placeholder="+213..."
                    />
                  </div>

                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700">Statut</label>
                    <select
                      value={formData.status}
                      onChange={(event) => setFormData((current) => ({ ...current, status: event.target.value }))}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
                    >
                      <option value="active">Actif</option>
                      <option value="inactive">Inactif</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
                <div className="mb-4 flex items-center justify-between gap-3">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Champs du formulaire</h3>
                    <p className="mt-1 text-sm text-gray-500">
                      Ajoute seulement les informations dont ton service a besoin.
                    </p>
                  </div>

                  <button
                    type="button"
                    onClick={() => addField(TopupTemplateService.FIELD_TYPES.TEXT)}
                    className="inline-flex items-center rounded-lg bg-primary-600 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-700"
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Ajouter un champ
                  </button>
                  <button
                    type="button"
                    onClick={() => addField(TopupTemplateService.FIELD_TYPES.INSTRUCTION)}
                    className="inline-flex items-center rounded-lg border border-primary-200 bg-white px-3 py-2 text-sm font-medium text-primary-700 transition-colors hover:bg-primary-50"
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Ajouter une instruction
                  </button>
                </div>

                <div className="space-y-4">
                  {formData.fields.length === 0 ? (
                    <div className="rounded-2xl border border-dashed border-gray-300 bg-white px-5 py-8 text-center">
                      <p className="text-sm font-medium text-gray-700">Ce template peut fonctionner sans champ.</p>
                      <p className="mt-1 text-sm text-gray-500">
                        Tu peux laisser le template vide, ajouter des champs de saisie ou seulement des instructions.
                      </p>
                    </div>
                  ) : formData.fields.map((field, index) => (
                    <div key={field.id} className="rounded-2xl border border-gray-200 bg-white p-4">
                      <div className="mb-4 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Settings2 className="h-4 w-4 text-primary-500" />
                          <p className="font-medium text-gray-900">
                            {field.type === TopupTemplateService.FIELD_TYPES.INSTRUCTION
                              ? `Instruction #${index + 1}`
                              : `Champ #${index + 1}`}
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeField(field.id)}
                          className="rounded-lg border border-red-200 px-3 py-1.5 text-sm font-medium text-red-600 transition-colors hover:bg-red-50"
                        >
                          Supprimer
                        </button>
                      </div>

                      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                        <div>
                          <label className="mb-2 block text-sm font-medium text-gray-700">
                            {field.type === TopupTemplateService.FIELD_TYPES.INSTRUCTION ? 'Titre *' : 'Libelle *'}
                          </label>
                          <input
                            type="text"
                            value={field.label}
                            onChange={(event) => updateField(field.id, 'label', event.target.value)}
                            className={`w-full rounded-lg border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500/20 ${
                              formErrors[`field_${index}_label`] ? 'border-red-300' : 'border-gray-300'
                            }`}
                            placeholder={field.type === TopupTemplateService.FIELD_TYPES.INSTRUCTION ? 'Ex: Avant de continuer' : 'Ex: Player ID'}
                          />
                          {formErrors[`field_${index}_label`] && (
                            <p className="mt-1 text-xs text-red-600">{formErrors[`field_${index}_label`]}</p>
                          )}
                        </div>

                        <div>
                          <label className="mb-2 block text-sm font-medium text-gray-700">Type</label>
                          <select
                            value={field.type}
                            onChange={(event) => updateField(field.id, 'type', event.target.value)}
                            className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
                          >
                            {Object.values(TopupTemplateService.FIELD_TYPES).map((type) => (
                              <option key={type} value={type}>
                                {TopupTemplateService.getFieldTypeLabel(type)}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className="xl:col-span-2">
                          <label className="mb-2 block text-sm font-medium text-gray-700">Placeholder</label>
                          <input
                            type="text"
                            value={field.placeholder}
                            onChange={(event) => updateField(field.id, 'placeholder', event.target.value)}
                            disabled={field.type === TopupTemplateService.FIELD_TYPES.INSTRUCTION}
                            className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 disabled:cursor-not-allowed disabled:bg-gray-100"
                            placeholder={field.type === TopupTemplateService.FIELD_TYPES.INSTRUCTION ? 'Non utilise pour une instruction' : 'Ex: Entrez le player ID'}
                          />
                        </div>
                      </div>

                      <div className="mt-4 grid gap-4 md:grid-cols-2">
                        <div>
                          <label className="mb-2 block text-sm font-medium text-gray-700">
                            {field.type === TopupTemplateService.FIELD_TYPES.INSTRUCTION ? 'Contenu de l instruction *' : 'Texte d aide'}
                          </label>
                          <textarea
                            value={field.helpText}
                            onChange={(event) => updateField(field.id, 'helpText', event.target.value)}
                            rows={field.type === TopupTemplateService.FIELD_TYPES.INSTRUCTION ? 4 : 2}
                            className={`w-full rounded-lg border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500/20 ${
                              formErrors[`field_${index}_helpText`] ? 'border-red-300' : 'border-gray-300'
                            }`}
                            placeholder={field.type === TopupTemplateService.FIELD_TYPES.INSTRUCTION ? 'Ex: Collez votre lien de profil public ou prenez une capture complete avant validation.' : 'Ex: Copiez l identifiant exact du jeu'}
                          />
                          {formErrors[`field_${index}_helpText`] && (
                            <p className="mt-1 text-xs text-red-600">{formErrors[`field_${index}_helpText`]}</p>
                          )}
                        </div>

                        <div className="flex items-end">
                          <label className="inline-flex items-center gap-3 rounded-xl border border-gray-200 px-4 py-3 text-sm font-medium text-gray-700">
                            <input
                              type="checkbox"
                              checked={Boolean(field.required)}
                              onChange={(event) => updateField(field.id, 'required', event.target.checked)}
                              disabled={field.type === TopupTemplateService.FIELD_TYPES.INSTRUCTION}
                              className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                            />
                            Champ obligatoire
                          </label>
                        </div>
                      </div>

                      {field.type === TopupTemplateService.FIELD_TYPES.SELECT && (
                        <div className="mt-4">
                          <label className="mb-2 block text-sm font-medium text-gray-700">
                            Options de la liste *
                          </label>
                          <textarea
                            value={field.optionsText}
                            onChange={(event) => updateField(field.id, 'optionsText', event.target.value)}
                            rows={4}
                            className={`w-full rounded-lg border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500/20 ${
                              formErrors[`field_${index}_options`] ? 'border-red-300' : 'border-gray-300'
                            }`}
                            placeholder={'Instagram\nTikTok\nFacebook'}
                          />
                          {formErrors[`field_${index}_options`] && (
                            <p className="mt-1 text-xs text-red-600">{formErrors[`field_${index}_options`]}</p>
                          )}
                          <p className="mt-1 text-xs text-gray-400">Une option par ligne.</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 border-t border-gray-200 pt-4">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="rounded-lg border border-gray-300 px-4 py-2 text-gray-600 transition-colors hover:bg-gray-50"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="inline-flex items-center rounded-lg bg-primary-600 px-4 py-2 text-white transition-colors hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {submitting ? (
                    <>
                      <LoadingSpinner size="sm" className="mr-2" />
                      Sauvegarde...
                    </>
                  ) : editingTemplate ? 'Mettre a jour le template' : 'Creer le template'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TopupTemplateManagement;
